import { formatUserFacingError, withErrorCode } from '@/lib/errors'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { ClientUserReassignModal } from './ClientUserReassignModal'
import { EngagementDetailsView } from './EngagementDetailsView'
import { EngagementDriveFolder } from './EngagementDriveFolder'
import { LineItemsEditor } from './LineItemsEditor'
import { cancelEngagementFn } from '@/lib/firebase/functions'
import type { ClientEngagementDocument, CompanyMemberDocument, EquipmentDocument, UserDocument, EngagementStatus } from '@/types'
import type { EngagementFormValues } from '../types'

const STATUS_OPTIONS: { value: EngagementStatus; label: string }[] = [
  { value: 'scheduled',           label: 'Scheduled'             },
  { value: 'awaiting_acceptance', label: 'Awaiting Acceptance'   },
  { value: 'awaiting_payment',    label: 'Awaiting Deposit'      },
  { value: 'confirmed',           label: 'Confirmed'             },
  { value: 'in_progress',         label: 'In Progress'           },
  { value: 'delivered',           label: 'Delivered'             },
  { value: 'cancelled',           label: 'Cancelled'             },
]

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  engagement?: ClientEngagementDocument & { id: string }
  initialValues?: Partial<EngagementFormValues>
  members: Array<CompanyMemberDocument & { id: string }>
  equipment: Array<EquipmentDocument & { id: string }>
  /** Member IDs who marked themselves Available on the source request (sorts them first). */
  availableMemberIds?: string[]
  onSave: (data: EngagementFormValues) => Promise<void>
  onLookupUser: (email: string) => Promise<(UserDocument & { id: string }) | null>
  onCreateClient: (email: string, displayName: string) => Promise<UserDocument & { id: string }>
  onRefresh?: () => void
}

const DEFAULT_VALUES: EngagementFormValues = {
  clientUserId: '',
  clientEmail: '',
  clientName: '',
  title: '',
  description: '',
  assignedMemberIds: [],
  equipmentIds: [],
  scheduledDate: '',
  estimateNote: '',
  driveLink: '',
  status: 'scheduled',
  sourceRequestId: '',
  lineItems: [],
  discountPercent: 0,
}

export function ClientEngagementModal({
  open,
  onOpenChange,
  engagement,
  initialValues,
  members,
  equipment,
  availableMemberIds = [],
  onSave,
  onLookupUser,
  onCreateClient,
  onRefresh,
}: Props) {
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // 'view' = read-only (default when opening an existing engagement so admins
  // can inspect without risk of editing). 'edit' = unlocked (default for new).
  const [mode, setMode] = useState<'view' | 'edit'>('view')

  // User lookup state
  const [lookupEmail, setLookupEmail] = useState('')
  const [lookupStatus, setLookupStatus] = useState<'idle' | 'loading' | 'found' | 'not_found'>('idle')
  const [lookedUpUser, setLookedUpUser] = useState<(UserDocument & { id: string }) | null>(null)

  // Inline client creation state (shown when lookup returns not_found)
  const [createName, setCreateName] = useState('')
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  const [createSuccess, setCreateSuccess] = useState(false)

  // Cancel-engagement state
  const [cancelling, setCancelling] = useState(false)
  const [cancelError, setCancelError] = useState<string | null>(null)

  // 72-hour minimum lead time override (admin-only escape hatch).
  const [leadTimeOverrideAck, setLeadTimeOverrideAck] = useState(false)

  async function handleCancelEngagement() {
    if (!engagement) return
    setCancelling(true); setCancelError(null)
    try {
      await cancelEngagementFn({ engagementId: engagement.id })
      onRefresh?.()
      onOpenChange(false)
    } catch (err) {
      setCancelError(formatUserFacingError(err, 'Failed to cancel engagement.'))
    } finally {
      setCancelling(false)
    }
  }

  // Reassign confirmation (edit mode only)
  const [reassignPending, setReassignPending] = useState<{
    newUserId: string
    newEmail: string
    newName: string
  } | null>(null)

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<EngagementFormValues>({ defaultValues: DEFAULT_VALUES })

  const statusValue = watch('status')
  const assignedMemberIds = watch('assignedMemberIds') ?? []
  const equipmentIds = watch('equipmentIds')
  const clientEmail = watch('clientEmail')
  const scheduledDate = watch('scheduledDate')

  // Date-only comparison: warn if scheduledDate is fewer than 3 full calendar
  // days from today. Border cases (e.g. Day+3 at 9am vs now at noon) are
  // handled by the time-aware check on the service request form.
  const leadTimeViolation = (() => {
    if (!scheduledDate) return false
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const minDate = new Date(today.getTime() + 3 * 24 * 60 * 60 * 1000)
    const booking = new Date(`${scheduledDate}T00:00:00`)
    return !isNaN(booking.getTime()) && booking < minDate
  })()

  useEffect(() => {
    if (open) {
      // Existing engagements open in read-only view mode (compact summary
      // cards). New engagements open in edit mode.
      setMode(engagement ? 'view' : 'edit')
      setError(null)
      setLookupStatus('idle')
      setLookedUpUser(null)
      setReassignPending(null)
      setCreateError(null)
      setCreateSuccess(false)
      setCreating(false)
      setCreateName(initialValues?.clientName ?? '')
      setLeadTimeOverrideAck(false)
      if (engagement) {
        setLookupEmail(engagement.clientEmail)
        reset({
          clientUserId: engagement.clientUserId,
          clientEmail: engagement.clientEmail,
          clientName: engagement.clientName,
          title: engagement.title,
          description: engagement.description ?? '',
          assignedMemberIds: engagement.assignedMemberIds ?? (engagement.assignedMemberId ? [engagement.assignedMemberId] : []),
          equipmentIds: engagement.equipmentIds ?? [],
          scheduledDate: engagement.scheduledDate ?? '',
          estimateNote: engagement.estimateNote ?? '',
          driveLink: engagement.driveLink ?? '',
          status: engagement.status,
          sourceRequestId: engagement.sourceRequestId ?? '',
          lineItems: engagement.lineItems ?? [],
          discountPercent: engagement.discountPercent ?? 0,
        })
      } else {
        const base = { ...DEFAULT_VALUES, ...initialValues }
        setLookupEmail(base.clientEmail ?? '')
        reset(base)
      }
    }
  }, [open, engagement, initialValues, reset])

  async function handleCreateClient() {
    const email = lookupEmail.trim().toLowerCase()
    const name = createName.trim()
    if (!email || !name) {
      setCreateError(withErrorCode('Email and name are required.', 'app/validation'))
      return
    }
    setCreating(true)
    setCreateError(null)
    try {
      const user = await onCreateClient(email, name)
      setLookedUpUser(user)
      setLookupStatus('found')
      setCreateSuccess(true)
      setValue('clientUserId', user.id)
      setValue('clientEmail', user.email)
      setValue('clientName', user.displayName ?? user.email)
      setLookupEmail(user.email)
    } catch (err) {
      setCreateError(formatUserFacingError(err, 'Failed to create client account.'))
    } finally {
      setCreating(false)
    }
  }

  async function handleLookup() {
    const email = lookupEmail.trim()
    if (!email) return
    setLookupStatus('loading')
    setLookedUpUser(null)
    setCreateError(null)
    setCreateSuccess(false)
    try {
      const user = await onLookupUser(email)
      if (user) {
        setLookedUpUser(user)
        setLookupStatus('found')

        if (engagement) {
          // Edit mode — require confirmation before changing the linked user
          if (user.id !== engagement.clientUserId) {
            setReassignPending({
              newUserId: user.id,
              newEmail: user.email,
              newName: user.displayName ?? user.email,
            })
          }
        } else {
          // Create mode — set immediately
          setValue('clientUserId', user.id)
          setValue('clientEmail', user.email)
          setValue('clientName', user.displayName ?? user.email)
        }
      } else {
        setLookupStatus('not_found')
      }
    } catch {
      setLookupStatus('not_found')
    }
  }

  function handleReassignConfirm() {
    if (!reassignPending) return
    setValue('clientUserId', reassignPending.newUserId)
    setValue('clientEmail', reassignPending.newEmail)
    setValue('clientName', reassignPending.newName)
    setLookupEmail(reassignPending.newEmail)
    setReassignPending(null)
  }

  function handleReassignCancel() {
    setReassignPending(null)
    setLookupEmail(clientEmail)
    setLookupStatus('idle')
    setLookedUpUser(null)
  }

  function toggleEquipment(id: string) {
    const current = equipmentIds ?? []
    if (current.includes(id)) {
      setValue('equipmentIds', current.filter((x) => x !== id))
    } else {
      setValue('equipmentIds', [...current, id])
    }
  }

  async function onSubmit(data: EngagementFormValues) {
    if (!data.clientUserId) {
      setError(withErrorCode('Please look up and confirm a client before saving.', 'app/validation'))
      return
    }
    if (leadTimeViolation && !leadTimeOverrideAck) {
      setError(withErrorCode('Scheduled date is under the 72-hour minimum lead time. Acknowledge the override below to save anyway.', 'app/validation'))
      return
    }
    setSaving(true)
    setError(null)
    try {
      await onSave(data)
      onOpenChange(false)
    } catch (err) {
      setError(formatUserFacingError(err, 'Failed to save engagement.'))
    } finally {
      setSaving(false)
    }
  }

  const isEdit = !!engagement

  return (
    <>
      <Dialog open={open && !reassignPending} onOpenChange={onOpenChange}>
        <DialogContent
          className="
            flex flex-col gap-0 p-0
            w-[95vw] sm:w-[90vw] max-w-6xl max-h-[90vh]
          "
        >
          <DialogHeader className="border-b px-6 py-4">
            <DialogTitle>
              {!isEdit
                ? 'New Engagement'
                : mode === 'view'
                  ? 'Engagement Details'
                  : 'Edit Engagement'}
            </DialogTitle>
          </DialogHeader>

          <form
            onSubmit={(e) => void handleSubmit(onSubmit)(e)}
            className="flex flex-1 flex-col overflow-hidden"
          >
            {isEdit && mode === 'view' && engagement ? (
              <div
                className="
                  flex-1 overflow-y-auto px-6 py-5
                  [scrollbar-width:thin] [scrollbar-color:hsl(var(--muted-foreground)/0.35)_transparent]
                  [&::-webkit-scrollbar]:w-1.5
                  [&::-webkit-scrollbar-track]:bg-transparent
                  [&::-webkit-scrollbar-thumb]:rounded-full
                  [&::-webkit-scrollbar-thumb]:bg-muted-foreground/30
                  hover:[&::-webkit-scrollbar-thumb]:bg-muted-foreground/50
                "
              >
                <EngagementDetailsView
                  engagement={engagement}
                  members={members}
                  equipment={equipment}
                  onRefresh={() => onRefresh?.()}
                />
              </div>
            ) : (
            <div
              className="
                flex-1 overflow-y-auto px-6 py-5
                grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-5
                [scrollbar-width:thin] [scrollbar-color:hsl(var(--muted-foreground)/0.35)_transparent]
                [&::-webkit-scrollbar]:w-1.5
                [&::-webkit-scrollbar-track]:bg-transparent
                [&::-webkit-scrollbar-thumb]:rounded-full
                [&::-webkit-scrollbar-thumb]:bg-muted-foreground/30
                hover:[&::-webkit-scrollbar-thumb]:bg-muted-foreground/50
              "
            >
            {/* ── Client Lookup ─────────────────────────────────────── */}
            <div className="space-y-2 lg:col-span-2">
              <Label>Client *</Label>

              {isEdit && clientEmail && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground">Current:</span>
                  <Badge variant="secondary">{clientEmail}</Badge>
                  <span className="text-xs text-muted-foreground">
                    ({watch('clientName')})
                  </span>
                </div>
              )}

              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="client@example.com"
                  value={lookupEmail}
                  onChange={(e) => { setLookupEmail(e.target.value); setLookupStatus('idle') }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void handleLookup() } }}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => void handleLookup()}
                  disabled={lookupStatus === 'loading' || !lookupEmail.trim()}
                >
                  <Search className="h-4 w-4 mr-1" />
                  Find
                </Button>
              </div>

              {lookupStatus === 'found' && lookedUpUser && !reassignPending && (
                <p className="text-xs text-emerald-600">
                  ✓ Found: {lookedUpUser.displayName ?? lookedUpUser.email}
                </p>
              )}
              {lookupStatus === 'not_found' && (
                <div className="rounded-md border border-dashed p-3 space-y-2">
                  <p className="text-xs text-destructive">
                    {withErrorCode('No user was found with that email address.', 'admin/client-not-found')}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Create a client account for them — they'll receive an email to set their password.
                  </p>
                  <div className="space-y-1">
                    <Label htmlFor="new-client-name" className="text-xs">Client name *</Label>
                    <Input
                      id="new-client-name"
                      placeholder="Jane Doe"
                      value={createName}
                      onChange={(e) => setCreateName(e.target.value)}
                    />
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => void handleCreateClient()}
                    disabled={creating || !createName.trim() || !lookupEmail.trim()}
                  >
                    {creating ? 'Creating…' : 'Create client account & link'}
                  </Button>
                  {createError && (
                    <p className="text-xs text-destructive">{createError}</p>
                  )}
                </div>
              )}
              {createSuccess && lookupStatus === 'found' && (
                <p className="text-xs text-emerald-600">
                  ✓ Account created — invite email sent to {lookupEmail}.
                </p>
              )}
              {!watch('clientUserId') && (
                <p className="text-xs text-muted-foreground">
                  Enter the client's email and click Find to link their account.
                </p>
              )}
            </div>

            {/* ── Title ─────────────────────────────────────────────── */}
            <div className="space-y-1 lg:col-span-2">
              <Label htmlFor="eng-title">Title *</Label>
              <Input
                id="eng-title"
                placeholder="Wedding Videography Package"
                {...register('title', { required: 'Title is required.' })}
              />
              {errors.title && (
                <p className="text-xs text-destructive">{withErrorCode(String(errors.title.message), 'app/validation')}</p>
              )}
            </div>

            {/* ── Description ───────────────────────────────────────── */}
            <div className="space-y-1 lg:col-span-2">
              <Label htmlFor="eng-desc">Description</Label>
              <Textarea
                id="eng-desc"
                placeholder="Brief description of the services being rendered…"
                rows={3}
                {...register('description')}
              />
            </div>

            {/* ── Status ────────────────────────────────────────────── */}
            <div className="space-y-1">
              <Label>Status *</Label>
              <Select
                value={statusValue}
                onValueChange={(v) => setValue('status', v as EngagementStatus)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* ── Service Providers (multi) ─────────────────────────── */}
            <div className="space-y-2">
              <Label>Service Providers</Label>
              <p className="text-xs text-muted-foreground">
                Assign one or more team members. Members who marked themselves Available on the source request appear first.
              </p>
              <div className="max-h-48 overflow-y-auto rounded-md border p-3 space-y-1.5">
                {members.filter((m) => m.isActive).length === 0 ? (
                  <p className="text-xs text-muted-foreground">No active members.</p>
                ) : (
                  members
                    .filter((m) => m.isActive)
                    .sort((a, b) => {
                      // Sort: those who marked Available first, then alphabetical
                      const aAvailable = availableMemberIds.includes(a.id) ? -1 : 1
                      const bAvailable = availableMemberIds.includes(b.id) ? -1 : 1
                      if (aAvailable !== bAvailable) return aAvailable - bAvailable
                      return a.name.localeCompare(b.name)
                    })
                    .map((m) => {
                      const isAvailable = availableMemberIds.includes(m.id)
                      const isAssigned = assignedMemberIds.includes(m.id)
                      return (
                        <label key={m.id} className="flex items-center gap-2 text-sm cursor-pointer">
                          <Checkbox
                            checked={isAssigned}
                            onCheckedChange={(checked) => {
                              const next = checked === true
                                ? [...assignedMemberIds, m.id]
                                : assignedMemberIds.filter((id) => id !== m.id)
                              setValue('assignedMemberIds', next, { shouldDirty: true })
                            }}
                          />
                          <span>{m.name}</span>
                          {isAvailable && (
                            <Badge variant="default" className="text-[10px] py-0">Available</Badge>
                          )}
                        </label>
                      )
                    })
                )}
              </div>
            </div>

            {/* ── Equipment / Technology ────────────────────────────── */}
            {equipment.length > 0 && (
              <div className="space-y-2 lg:col-span-2">
                <Label>Equipment / Technology</Label>
                <div className="max-h-40 overflow-y-auto rounded-md border p-3 space-y-2">
                  {equipment.map((item) => (
                    <div key={item.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`eq-${item.id}`}
                        checked={(equipmentIds ?? []).includes(item.id)}
                        onCheckedChange={() => toggleEquipment(item.id)}
                      />
                      <label
                        htmlFor={`eq-${item.id}`}
                        className="text-sm cursor-pointer leading-none"
                      >
                        {item.name}
                        {item.category && (
                          <span className="ml-1 text-muted-foreground text-xs">
                            ({item.category})
                          </span>
                        )}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Scheduled Date ───────────────────────────────────── */}
            <div className="space-y-1">
              <Label htmlFor="eng-date">Scheduled Date</Label>
              <Input id="eng-date" type="date" {...register('scheduledDate')} />
              {leadTimeViolation && (
                <div className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3 space-y-2">
                  <p className="text-xs text-amber-700 dark:text-amber-200">
                    {withErrorCode(
                      "This date is within the 72-hour minimum lead time. Public bookings can't be made this close — this is an admin override path. Confirm below to save anyway.",
                      'request/lead-time-override',
                    )}
                  </p>
                  <label className="flex items-start gap-2 text-xs cursor-pointer">
                    <Checkbox
                      checked={leadTimeOverrideAck}
                      onCheckedChange={(v) => setLeadTimeOverrideAck(v === true)}
                    />
                    <span>
                      I understand this booking is under the minimum lead time and I'm proceeding intentionally.
                    </span>
                  </label>
                </div>
              )}
            </div>

            {/* ── Drive Folder (auto-provisioned, with manual override) ── */}
            <div className="space-y-1">
              <Label>Google Drive Folder</Label>
              <EngagementDriveFolder
                engagementId={engagement?.id ?? null}
                driveFolderUrl={engagement?.driveFolderUrl}
                driveFolderName={engagement?.driveFolderName}
                legacyDriveLink={engagement?.driveLink}
                onCreated={onRefresh}
              />
            </div>

            {/* ── Line Items + Discount + Total ────────────────────── */}
            <div className="lg:col-span-2">
              <LineItemsEditor
                lineItems={watch('lineItems')}
                discountPercent={watch('discountPercent')}
                onChange={(next) => {
                  setValue('lineItems', next.lineItems, { shouldDirty: true })
                  setValue('discountPercent', next.discountPercent, { shouldDirty: true })
                }}
              />
            </div>

            <div className="space-y-1 lg:col-span-2">
              <Label htmlFor="eng-note">Estimate Note</Label>
              <Input
                id="eng-note"
                placeholder="e.g. Includes color grading"
                {...register('estimateNote')}
              />
            </div>

            {/* ── Source Request (read-only) ─────────────────────────── */}
            {isEdit && engagement?.sourceRequestId && (
              <div className="space-y-1 lg:col-span-2">
                <Label className="text-muted-foreground text-xs">Source Request ID</Label>
                <p className="text-sm font-mono text-muted-foreground">
                  {engagement.sourceRequestId}
                </p>
              </div>
            )}

            {/* Billing actions use saved Firestore data. Keep them in view mode
                so an unsaved edit can never be invoiced accidentally. */}
            {isEdit && engagement?.estimateAmount && (
              <div className="lg:col-span-2 rounded-md border bg-muted/30 p-3">
                <p className="text-sm font-medium">Save before billing</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Invoice actions are available in the engagement details view after your changes are saved.
                </p>
              </div>
            )}

            {error && (
              <p className="text-sm text-destructive lg:col-span-2">{error}</p>
            )}
            </div>
            )}

            <DialogFooter className="border-t px-6 py-3 flex sm:justify-between">
              <div>
                {isEdit && engagement && engagement.status !== 'cancelled' && mode === 'edit' && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button type="button" variant="ghost" className="text-destructive hover:text-destructive" disabled={cancelling}>
                        {cancelling ? 'Cancelling…' : 'Cancel Engagement'}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Cancel this engagement?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This sets the engagement status to <strong>cancelled</strong> and removes any
                          pending reminder and final-invoice jobs. Any unpaid Stripe invoices are voided;
                          paid invoices are preserved and are never refunded automatically.
                          <br /><br />
                          This cannot be undone from this UI.
                          {cancelError && <span className="block mt-3 text-destructive text-xs">{cancelError}</span>}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Keep Engagement</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => void handleCancelEngagement()}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Yes, Cancel It
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
              <div className="flex gap-2">
                {isEdit && mode === 'view' ? (
                  <>
                    <Button key="view-close" type="button" variant="outline" onClick={() => onOpenChange(false)}>
                      Close
                    </Button>
                    <Button key="view-edit" type="button" onClick={() => setMode('edit')}>
                      Edit
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      key="edit-cancel"
                      type="button"
                      variant="outline"
                      onClick={() => {
                        if (isEdit) {
                          // Reset form to current engagement state and drop back to view mode.
                          if (engagement) {
                            reset({
                              clientUserId: engagement.clientUserId,
                              clientEmail: engagement.clientEmail,
                              clientName: engagement.clientName,
                              title: engagement.title,
                              description: engagement.description ?? '',
                              assignedMemberIds: engagement.assignedMemberIds ?? (engagement.assignedMemberId ? [engagement.assignedMemberId] : []),
                              equipmentIds: engagement.equipmentIds ?? [],
                              scheduledDate: engagement.scheduledDate ?? '',
                              estimateNote: engagement.estimateNote ?? '',
                              driveLink: engagement.driveLink ?? '',
                              status: engagement.status,
                              sourceRequestId: engagement.sourceRequestId ?? '',
                              lineItems: engagement.lineItems ?? [],
                              discountPercent: engagement.discountPercent ?? 0,
                            })
                          }
                          setError(null)
                          setMode('view')
                        } else {
                          onOpenChange(false)
                        }
                      }}
                    >
                      {isEdit ? 'Cancel' : 'Close'}
                    </Button>
                    <Button key="edit-save" type="submit" disabled={saving}>
                      {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Engagement'}
                    </Button>
                  </>
                )}
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reassign confirmation — shown over the main modal */}
      {reassignPending && (
        <ClientUserReassignModal
          open
          oldEmail={engagement?.clientEmail ?? ''}
          newEmail={reassignPending.newEmail}
          onConfirm={handleReassignConfirm}
          onCancel={handleReassignCancel}
        />
      )}
    </>
  )
}
